import React, { useState, useEffect, useCallback } from "react";
import { Button } from "../../../../ui/components/Button";
import { IconButton } from "../../../../ui/components/IconButton";
import { TextField } from "../../../../ui/components/TextField";
import { DialogLayout } from "../../../../ui/layouts/DialogLayout";
import { FeatherX } from "@subframe/core";
import { usePermissions } from "../../../../shared/presentation/hooks/usePermissions";
import {
  MODULE_CODES,
  ACTION_CODES,
} from "../../../../shared/constants/permissions";
import { ReviewRequestModal } from "../../../../shared/presentation/components/ReviewRequestModal";
import {
  useToast,
  GlAccountTypePicker,
  GlParentAccountDropdown,
} from "../../../../shared/presentation/components";
import { useGls } from "../hooks/useGls";
import type { GlEntity } from "../../domain/entities/GlEntity";
import { getAccountNameLeaf } from "../../../../utils";
import { isReservedTallyPrimaryGroupName } from "../utils/tallyGlDefaults";

/** Account types hidden from the GL form dropdown. */
const EXCLUDED_ACCOUNT_TYPES = [
  "bank",
  "accounts_receivable",
  "accounts_payable",
];

export type GlGroupFormSuccessParams =
  | { mode: "create"; sentForReview: boolean }
  | { mode: "edit"; sentForReview: false; gl: GlEntity }
  | { mode: "edit"; sentForReview: true; accountId: string };

export interface GlGroupFormModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: (params: GlGroupFormSuccessParams) => void;
  mode: "create" | "edit";
  initialGl?: GlEntity | null;
}

export const GlGroupFormModal: React.FC<GlGroupFormModalProps> = ({
  open,
  onOpenChange,
  onSuccess,
  mode,
  initialGl,
}) => {
  const { getPermissionInfo } = usePermissions();
  const { showSuccess, showError } = useToast();
  const {
    createGl,
    updateGl,
    getAccountTypeGroups,
    accountTypeGroups,
    accountTypeGroupsLoading,
  } = useGls();

  const actionCode =
    mode === "create" ? ACTION_CODES.CREATE : ACTION_CODES.EDIT;
  const { granted, requiresReview } = getPermissionInfo(
    MODULE_CODES.GL,
    actionCode,
  );

  const [groupName, setGroupName] = useState("");
  const [accountType, setAccountType] = useState("");
  const [isSubGroup, setIsSubGroup] = useState(false);
  const [parentAccountId, setParentAccountId] = useState<string | null>(null);
  const [parentAccountName, setParentAccountName] = useState("");
  const [parentAccountFullPath, setParentAccountFullPath] = useState("");
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isReviewModalOpen, setIsReviewModalOpen] = useState(false);
  const [reviewAction, setReviewAction] = useState<"create" | "edit">("create");

  const accountTypeGroupMap = accountTypeGroups?.groups ?? {};
  const accountTypeLabels = accountTypeGroups?.labels ?? {};

  const resetForm = useCallback(() => {
    if (mode === "edit" && initialGl) {
      setGroupName(initialGl.glName);
      setAccountType(initialGl.accountType);
      setIsSubGroup(!!initialGl.parentId);
      setParentAccountId(initialGl.parentId ?? null);
      setParentAccountName(
        initialGl.parentName ? getAccountNameLeaf(initialGl.parentName) : "",
      );
      setParentAccountFullPath(initialGl.parentName ?? "");
    } else {
      setGroupName("");
      setAccountType("");
      setIsSubGroup(false);
      setParentAccountId(null);
      setParentAccountName("");
      setParentAccountFullPath("");
    }
    setErrors({});
  }, [mode, initialGl]);

  useEffect(() => {
    if (open) {
      resetForm();
      getAccountTypeGroups();
    }
  }, [open, resetForm, getAccountTypeGroups]);

  const handleClose = () => {
    if (!isSubmitting) {
      setErrors({});
      onOpenChange(false);
    }
  };

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!groupName?.trim()) newErrors.groupName = "Group name is required";
    if (
      groupName.trim() &&
      isReservedTallyPrimaryGroupName(groupName.trim())
    ) {
      newErrors.groupName =
        "Cannot use a reserved Tally primary group name";
    }
    if (!accountType) newErrors.accountType = "Account type is required";
    if (isSubGroup && !parentAccountId) {
      newErrors.parentAccountId =
        "Parent group is required when creating a sub group";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const buildChangeDetails = (action: "create" | "edit") => {
    const raw: Record<string, unknown> = {
      account_name: groupName.trim(),
      account_type: accountType,
      parent_account_id: isSubGroup ? parentAccountId : null,
      parent_account_name:
        parentAccountFullPath || parentAccountName || undefined,
      node_kind: "group",
    };
    if (mode === "edit" && initialGl) raw.account_id = initialGl.accountId;
    const changeDetails: {
      action_types: string[];
      raw_json: Record<string, unknown>;
      field_changes?: Array<{ field: string; old: string; new: string }>;
    } = {
      action_types: [action],
      raw_json: raw,
    };
    if (action === "edit" && initialGl) {
      const fc: Array<{ field: string; old: string; new: string }> = [];
      if (initialGl.glName !== groupName.trim()) {
        fc.push({
          field: "account_name",
          old: initialGl.glName,
          new: groupName.trim(),
        });
      }
      if (initialGl.accountType !== accountType) {
        fc.push({
          field: "account_type",
          old: initialGl.accountType,
          new: accountType,
        });
      }
      if ((initialGl.parentId ?? null) !== (isSubGroup ? parentAccountId : null)) {
        fc.push({
          field: "parent_account_id",
          old: initialGl.parentName || "None",
          new:
            isSubGroup && parentAccountFullPath
              ? parentAccountFullPath
              : isSubGroup && parentAccountName
                ? parentAccountName
                : "None",
        });
      }
      changeDetails.field_changes = fc;
    }
    return changeDetails;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    if (requiresReview) {
      setReviewAction(mode === "create" ? "create" : "edit");
      setIsReviewModalOpen(true);
      return;
    }

    setIsSubmitting(true);
    setErrors({});

    try {
      const payload = {
        account_name: groupName.trim(),
        account_type: accountType,
        parent_account_id: isSubGroup ? parentAccountId : null,
        node_kind: "group" as const,
      };
      if (mode === "create") {
        await createGl(payload);
        showSuccess("Group created successfully");
        onSuccess?.({ mode: "create", sentForReview: false });
      } else if (initialGl) {
        await updateGl(initialGl.accountId, payload);
        showSuccess("Group updated successfully");
        const updatedGl: GlEntity = {
          ...initialGl,
          glName: groupName.trim(),
          accountType,
          parentId: isSubGroup ? parentAccountId : null,
          parentName: isSubGroup
            ? parentAccountFullPath || parentAccountName || null
            : null,
          isGroup: true,
          invoice: false,
          bill: false,
        };
        onSuccess?.({ mode: "edit", sentForReview: false, gl: updatedGl });
      }
      handleClose();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : "Operation failed";
      showError(message);
      setErrors({ submit: message });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <DialogLayout open={open} onOpenChange={onOpenChange}>
        <div className="flex max-h-[90vh] w-144 flex-col items-start gap-6 overflow-hidden bg-white px-8 py-8">
          <div className="flex w-full items-center justify-between">
            <span className="text-heading-2 font-heading-2 text-default-font">
              {mode === "create" ? "Create Group" : "Edit Group"}
            </span>
            <IconButton
              icon={<FeatherX />}
              onClick={handleClose}
              disabled={isSubmitting}
            />
          </div>

          <div className="flex w-full flex-1 flex-col items-start gap-5 overflow-y-auto pr-1">
            {errors.submit && (
              <div className="w-full rounded-md border border-error-200 bg-error-50 px-4 py-3">
                <span className="text-body font-body text-error-700">
                  {errors.submit}
                </span>
              </div>
            )}

            <div className="flex w-full flex-col gap-1">
              <TextField
                className="h-auto w-full flex-none"
                variant="filled"
                label="Group name *"
                helpText="Display name for this Tally group"
                error={!!errors.groupName}
              >
                <TextField.Input
                  placeholder="e.g. Marketing Expenses"
                  value={groupName}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    setGroupName(e.target.value);
                    if (errors.groupName)
                      setErrors((prev) => ({ ...prev, groupName: "" }));
                  }}
                />
              </TextField>
              {errors.groupName && (
                <span className="text-caption font-caption text-error-700">
                  {errors.groupName}
                </span>
              )}
            </div>

            <div className="flex w-full flex-col gap-1">
              <span className="text-body-bold font-body-bold text-default-font">
                Account type <span className="text-error-600">*</span>
              </span>
              <GlAccountTypePicker
                groups={accountTypeGroupMap}
                labels={accountTypeLabels}
                selectedAccountType={accountType || undefined}
                excludedAccountTypes={EXCLUDED_ACCOUNT_TYPES}
                loading={accountTypeGroupsLoading}
                onSelect={(nextType) => {
                  setAccountType(nextType);
                  setParentAccountId(null);
                  setParentAccountName("");
                  setParentAccountFullPath("");
                  if (errors.accountType) {
                    setErrors((prev) => ({ ...prev, accountType: "" }));
                  }
                }}
                placeholder="Select account type"
              />
              {errors.accountType && (
                <span className="text-caption font-caption text-error-700">
                  {errors.accountType}
                </span>
              )}
            </div>

            <label className="flex cursor-pointer items-center gap-2">
              <input
                type="checkbox"
                checked={isSubGroup}
                onChange={(e) => {
                  setIsSubGroup(e.target.checked);
                  if (!e.target.checked) {
                    setParentAccountId(null);
                    setParentAccountName("");
                    setParentAccountFullPath("");
                  }
                  if (errors.parentAccountId)
                    setErrors((prev) => ({ ...prev, parentAccountId: "" }));
                }}
                className="h-4 w-4 rounded border-neutral-300 accent-brand-600"
              />
              <span className="text-body font-body text-default-font">
                Create as sub group?
              </span>
            </label>

            {isSubGroup && (
              <div className="flex w-full flex-col gap-1">
                <span className="text-body-bold font-body-bold text-default-font">
                  Parent group <span className="text-error-600">*</span>
                </span>
                <span className="text-caption font-caption text-subtext-color">
                  Only groups of the selected account type are shown.
                </span>
                <GlParentAccountDropdown
                  accountType={accountType}
                  selectedAccountId={parentAccountId ?? undefined}
                  selectedAccountName={parentAccountName || undefined}
                  excludeAccountId={
                    mode === "edit" && initialGl
                      ? initialGl.accountId
                      : undefined
                  }
                  disabled={!accountType}
                  onSelect={(account) => {
                    setParentAccountId(account.account_id);
                    setParentAccountName(account.gl_name);
                    setParentAccountFullPath(
                      account.full_path ?? account.gl_name,
                    );
                    if (account.account_type) {
                      setAccountType(account.account_type);
                    }
                    if (errors.parentAccountId) {
                      setErrors((prev) => ({ ...prev, parentAccountId: "" }));
                    }
                  }}
                  placeholder={
                    accountType
                      ? "Select parent group"
                      : "Select an account type first"
                  }
                />
                {errors.parentAccountId && (
                  <span className="text-caption font-caption text-error-700">
                    {errors.parentAccountId}
                  </span>
                )}
              </div>
            )}
          </div>

          <div className="flex w-full items-center justify-end gap-2 border-t border-solid border-neutral-border pt-6">
            <Button
              variant="neutral-tertiary"
              onClick={handleClose}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={isSubmitting || !granted}
              loading={isSubmitting}
            >
              {requiresReview
                ? "Submit for review"
                : mode === "create"
                  ? "Create"
                  : "Update"}
            </Button>
          </div>
        </div>
      </DialogLayout>

      <ReviewRequestModal
        open={isReviewModalOpen}
        onOpenChange={setIsReviewModalOpen}
        moduleCode="gl"
        actionCode={reviewAction === "create" ? "create" : "edit"}
        entityType="gl"
        entityId={mode === "edit" && initialGl ? initialGl.id : 0}
        changeDetails={buildChangeDetails(reviewAction)}
        skipNavigation={true}
        onSuccess={() => {
          setIsReviewModalOpen(false);
          if (mode === "edit" && initialGl) {
            onSuccess?.({
              mode: "edit",
              sentForReview: true,
              accountId: initialGl.accountId,
            });
          } else {
            onSuccess?.({ mode: "create", sentForReview: true });
          }
          onOpenChange(false);
        }}
      />
    </>
  );
};
